import { useCallback } from "react";
import { scrollToBottomJust } from "../utils/scrollUtils";
import { createPlaceholderMessage } from "../helpers/messageHelpers";
import type { HcpMentionSendOptions } from "../types";

export const useMessageSending = ({
  targetMessageId,
  setIsHeaderVisible,
  setManualOverride,
  scrollToBottom,
  sendMessage,
  user,
  sendMessageSocket,
  setMessages,
  messagesContainerRef,
  messageTheme,
  updateMessageTheme,
  themeChangeFromMessageRef,
  cleanMessageArray,
  fetchInitialMessages,
  scrollToBottomInitial,
}: {
  targetMessageId: string | null;
  setIsHeaderVisible: (value: boolean) => void;
  setManualOverride: (value: boolean) => void;
  scrollToBottom: () => void | Promise<void>;
  sendMessage: (
    text: string,
    user: any,
    sendMessageSocket: any,
    hideFromUser: boolean,
    options?: HcpMentionSendOptions,
  ) => Promise<void>;
  user: any;
  sendMessageSocket: any;
  setMessages: (callback: (prev: any[]) => any[]) => void;
  messagesContainerRef: React.MutableRefObject<HTMLDivElement | null>;
  messageTheme: string | null;
  updateMessageTheme: (value: "lessons" | "both" | "chats") => void;
  themeChangeFromMessageRef: React.MutableRefObject<boolean>;
  cleanMessageArray: () => Promise<void>;
  fetchInitialMessages: (targetMessageId?: string) => Promise<any>;
  scrollToBottomInitial: (container: HTMLDivElement | null) => void;
}) => {
  const handleSendMessage = useCallback(
    async (
      text: string,
      createPlaceholder = false,
      hideFromUser = false,
      options?: HcpMentionSendOptions,
    ) => {
      setIsHeaderVisible(false);
      setManualOverride(true);

      if (text.trim() === "/crash_course") {
        const crashCourseMessage = {
          message_id: `crash_course_${Date.now()}`,
          text: "",
          title: "",
          type: "crash_course_form" as const,
          is_bot: true,
          user_id: user?.user_id || "",
          message_type: "default" as const,
          domain: user?.domain || "",
          focus: user?.focus || "",
          date: new Date().toISOString(),
          isPlaceholder: false,
          isLoading: false,
        };

        setMessages((prev) => [...prev, crashCourseMessage]);

        if (messagesContainerRef.current) {
          scrollToBottomJust(messagesContainerRef.current);
        }
        return;
      }

      if (messageTheme === "lessons" || messageTheme === "chats") {
        themeChangeFromMessageRef.current = true;

        updateMessageTheme("both");

        await cleanMessageArray();
        await new Promise((resolve) => setTimeout(resolve, 500));

        try {
          await fetchInitialMessages();
          await new Promise(requestAnimationFrame);
          await scrollToBottomInitial(messagesContainerRef.current);
          await new Promise((resolve) => setTimeout(resolve, 300));
        } catch (error) {
          console.error("Error fetching initial messages:", error);
        }
      }

      if (targetMessageId) {
        const result = scrollToBottom();
        if (result instanceof Promise) {
          await result;
        }
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }

      await sendMessage(text, user, sendMessageSocket, hideFromUser, options);

      if (createPlaceholder) {
        const placeholderMessage = createPlaceholderMessage();
        setMessages((prev) => [...prev, placeholderMessage]);
        await new Promise((resolve) => setTimeout(resolve, 700));
      }

      await new Promise((resolve) => setTimeout(resolve, 500));
      if (messagesContainerRef.current) {
        scrollToBottomJust(messagesContainerRef.current);
      }
    },
    [
      targetMessageId,
      setIsHeaderVisible,
      setManualOverride,
      scrollToBottom,
      sendMessage,
      user,
      sendMessageSocket,
      setMessages,
      messagesContainerRef,
      messageTheme,
      updateMessageTheme,
      themeChangeFromMessageRef,
      cleanMessageArray,
      fetchInitialMessages,
      scrollToBottomInitial,
    ]
  );

  return { handleSendMessage };
};
